import React, { useState } from 'react';
import { CreditCard, Building2, Wallet, Star, Trash2, MoreVertical, CheckCircle, Shield, Clock, DollarSign } from 'lucide-react';
import { Card, CardContent } from '@vaiu/ui/card';
import { Button } from '@vaiu/ui/button';
import { Badge } from '@vaiu/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@vaiu/ui/dropdown-menu';
import { Alert, AlertDescription } from '@vaiu/ui/alert';
import { PaymentMethod } from '@/services/platformApi';

// Import brand logos
import VisaLogo from '@/assets/brand-logos/visa-logo.svg';
import MastercardLogo from '@/assets/brand-logos/mastercard-logo.svg';
import AmexLogo from '@/assets/brand-logos/amex-logo.svg';
import DiscoverLogo from '@/assets/brand-logos/discover-logo.svg';
import JcbLogo from '@/assets/brand-logos/jcb-logo.svg';

interface PaymentMethodCardProps {
  method: PaymentMethod;
  onSetDefault: (methodId: string) => void;
  onDelete: (method: PaymentMethod) => void;
  showActions?: boolean;
}

const PaymentMethodCardRefactored: React.FC<PaymentMethodCardProps> = ({
  method,
  onSetDefault,
  onDelete,
  showActions = true
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const getIcon = (type: string, className: string = "w-6 h-6") => {
    switch (type) {
      case 'stripe':
        return <CreditCard className={className} />;
      case 'plaid':
        return <Building2 className={className} />;
      case 'paypal':
        return <Wallet className={className} />;
      default:
        return <CreditCard className={className} />;
    }
  };

  const getBrandLogo = (brand: string) => {
    const brandLower = brand?.toLowerCase();
    switch (brandLower) {
      case 'visa':
        return VisaLogo;
      case 'mastercard':
        return MastercardLogo;
      case 'amex':
      case 'american_express':
        return AmexLogo;
      case 'discover':
        return DiscoverLogo;
      case 'jcb':
        return JcbLogo;
      default:
        return null;
    }
  };

  const getDisplayName = (method: PaymentMethod) => {
    switch (method.type) {
      case 'stripe':
        return {
          primary: `${method.details.brand?.toUpperCase()} •••• ${method.details.last4}`,
          secondary: 'Credit Card'
        };
      case 'plaid':
        return {
          primary: method.details.accountName || 'Bank Account',
          secondary: `${method.details.accountType || 'Account'} • ACH`
        };
      case 'paypal':
        return {
          primary: method.details.email || 'PayPal Account',
          secondary: 'PayPal Wallet'
        };
      default:
        return {
          primary: 'Unknown Funding Source',
          secondary: 'Unknown Type'
        };
    }
  };

  const getMethodColor = (type: string) => {
    switch (type) {
      case 'stripe':
        return 'from-blue-500 to-purple-600';
      case 'plaid':
        return 'from-green-500 to-emerald-600';
      case 'paypal':
        return 'from-blue-600 to-blue-800';
      default:
        return 'from-gray-500 to-gray-600';
    }
  };

  const getMethodFeatures = (type: string) => {
    switch (type) {
      case 'stripe':
        return {
          speed: 'Instant',
          fee: '2.9% + $0.30',
          secure: true
        };
      case 'plaid':
        return {
          speed: '1-3 days',
          fee: 'Free',
          secure: true
        };
      case 'paypal':
        return {
          speed: 'Instant',
          fee: '2.5%',
          secure: true
        };
      default:
        return {
          speed: 'Unknown',
          fee: 'Unknown',
          secure: false
        };
    }
  };

  const displayInfo = getDisplayName(method);
  const features = getMethodFeatures(method.type);
  const brandLogo = method.type === 'stripe' ? getBrandLogo(method.details.brand) : null;

  return (
    <Card 
      className={`relative transition-all duration-200 ${
        isHovered ? 'shadow-lg border-primary/30' : 'hover:shadow-md'
      } ${method.isDefault ? 'ring-2 ring-green-500/20 bg-green-50/30' : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          {/* Left Side - Method Info */}
          <div className="flex items-start space-x-4 flex-1">
            {/* Visual Icon with Gradient or Brand Logo */}
            <div className={`p-4 rounded-2xl ${brandLogo ? 'bg-white border-2 border-gray-200' : `bg-gradient-to-br ${getMethodColor(method.type)} text-white`} relative overflow-hidden flex items-center justify-center`}>
              {brandLogo ? (
                <img src={brandLogo} alt={method.details.brand} className="w-8 h-5 object-contain" />
              ) : (
                <>
                  {getIcon(method.type, "w-6 h-6")}
                  {/* Subtle pattern overlay for non-brand cards */}
                  <div className="absolute inset-0 bg-white/10 rounded-2xl"></div>
                </>
              )}
            </div>

            {/* Method Details */}
            <div className="space-y-2 flex-1">
              <div className="flex items-center space-x-2">
                <h3 className="font-semibold text-foreground">{displayInfo.primary}</h3>
                {method.isDefault && (
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                    <Star className="w-3 h-3 mr-1 fill-current" />
                    Primary Source
                  </Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground">{displayInfo.secondary}</p>
              
              {/* Features Row */}
              <div className="flex items-center space-x-4 text-xs text-muted-foreground pt-1">
                <div className="flex items-center space-x-1">
                  <Clock className="w-3 h-3" />
                  <span>{features.speed}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <DollarSign className="w-3 h-3" />
                  <span>{features.fee}</span>
                </div>
                {features.secure && (
                  <div className="flex items-center space-x-1">
                    <Shield className="w-3 h-3 text-green-600" />
                    <span className="text-green-600">Secured</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Side - Actions Menu */}
          {showActions && (
            <div className="flex items-center space-x-2">
              {/* More Actions Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="p-2">
                    <MoreVertical className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  {!method.isDefault && (
                    <>
                      <DropdownMenuItem onClick={() => onSetDefault(method.id || '')}>
                        <Star className="w-4 h-4 mr-2" />
                        Set as Default
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  <DropdownMenuItem 
                    onClick={() => onDelete(method)}
                    className="text-red-600 focus:text-red-600"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Remove
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>

        {/* Status and Additional Info */}
        {method.active === false && (
          <Alert variant="destructive" className="mt-4">
            <AlertDescription>
              This payment method is currently inactive and cannot be used for transactions.
            </AlertDescription>
          </Alert>
        )}

        {/* Additional Details when Hovered */}
        {isHovered && (
          <div className="mt-4 pt-4 border-t bg-muted/30 -mx-6 px-6 -mb-6 pb-6">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Added on:</span>
                <p className="font-medium">
                  {new Date(method.createdAt).toLocaleDateString()}
                </p>
              </div>
              <div>
                <span className="text-muted-foreground">Status:</span>
                <div className="flex items-center space-x-1">
                  <CheckCircle className="w-3 h-3 text-green-600" />
                  <span className="text-green-600 font-medium">Active</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PaymentMethodCardRefactored; 